import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Activity, Task, TaskFilters, TaskState } from "../../types/tasks";
import { taskAPI } from "../../services/api";

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const getTasks = createAsyncThunk(
  "tasks/getTasks",
  async (filters: TaskFilters = {}, { rejectWithValue }) => {
    try {
      const res = await taskAPI.getTasks(filters as Record<string, unknown>);
      return res.data.data as { tasks: Task[]; pagination: TaskState["pagination"] };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to fetch tasks");
    }
  },
);

export const getTask = createAsyncThunk(
  "tasks/getTask",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await taskAPI.getTask(id);
      return res.data.data.task as Task;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Task not found");
    }
  },
);

export const createTask = createAsyncThunk(
  "tasks/createTask",
  async (data: Partial<Task>, { rejectWithValue }) => {
    try {
      const res = await taskAPI.createTask(data as Record<string, unknown>);
      return res.data.data.task as Task;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to create task");
    }
  },
);

export const updateTask = createAsyncThunk(
  "tasks/updateTask",
  async ({ id, data }: { id: string; data: Partial<Task> }, { rejectWithValue }) => {
    try {
      const res = await taskAPI.updateTask(id, data as Record<string, unknown>);
      return res.data.data.task as Task;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to update task");
    }
  },
);

export const deleteTask = createAsyncThunk(
  "tasks/deleteTask",
  async (id: string, { rejectWithValue }) => {
    try {
      await taskAPI.deleteTask(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to delete task");
    }
  },
);

export const createShipmentFromTask = createAsyncThunk(
  "tasks/createShipmentFromTask",
  async (
    {
      taskId,
      transport,
      clientId,
      clientName,
    }: {
      taskId: string;
      transport?: Record<string, unknown>;
      clientId?: string | null;
      clientName?: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await taskAPI.createShipmentFromTask(taskId, { transport, clientId, clientName });
      return res.data.data as { task: Task; shipmentId: string; shipmentNumber: string };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to create shipment");
    }
  },
);

export const createActivity = createAsyncThunk(
  "tasks/createActivity",
  async ({ taskId, data }: { taskId: string; data: Partial<Activity> }, { rejectWithValue }) => {
    try {
      const res = await taskAPI.createActivity(taskId, data as Record<string, unknown>);
      return { task: res.data.data.task as Task };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to create activity");
    }
  },
);

export const updateActivity = createAsyncThunk(
  "tasks/updateActivity",
  async (
    { taskId, activityId, data }: { taskId: string; activityId: string; data: Partial<Activity> },
    { rejectWithValue },
  ) => {
    try {
      const res = await taskAPI.updateActivity(taskId, activityId, data as Record<string, unknown>);
      return res.data.data.task as Task;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to update activity");
    }
  },
);

export const deleteActivity = createAsyncThunk(
  "tasks/deleteActivity",
  async ({ taskId, activityId }: { taskId: string; activityId: string }, { rejectWithValue }) => {
    try {
      const res = await taskAPI.deleteActivity(taskId, activityId);
      return res.data.data.task as Task;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to delete activity");
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: TaskState = {
  tasks: [],
  currentTask: null,
  loading: false,
  error: null,
  pagination: { page: 1, limit: 20, total: 0, pages: 0 },
};

const tasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearCurrentTask: (state) => { state.currentTask = null; },
    setCurrentTask: (state, action: PayloadAction<Task | null>) => { state.currentTask = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      // getTasks
      .addCase(getTasks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.tasks;
        state.pagination = action.payload.pagination;
      })
      .addCase(getTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch tasks";
      })
      // getTask
      .addCase(getTask.fulfilled, (state, action) => { state.currentTask = action.payload; })
      // createTask
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
        state.pagination.total += 1;
      })
      // updateTask
      .addCase(updateTask.fulfilled, (state, action) => {
        const i = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (i !== -1) state.tasks[i] = action.payload;
        if (state.currentTask?._id === action.payload._id) state.currentTask = action.payload;
      })
      // deleteTask
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((t) => t._id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      })
      // createShipmentFromTask
      .addCase(createShipmentFromTask.fulfilled, (state, action) => {
        const { task } = action.payload;
        const i = state.tasks.findIndex((t) => t._id === task._id);
        if (i !== -1) state.tasks[i] = task;
        if (state.currentTask?._id === task._id) state.currentTask = task;
      })
      // createActivity
      .addCase(createActivity.fulfilled, (state, action) => {
        const { task } = action.payload;
        const i = state.tasks.findIndex((t) => t._id === task._id);
        if (i !== -1) state.tasks[i] = task;
        if (state.currentTask?._id === task._id) state.currentTask = task;
      })
      // updateActivity
      .addCase(updateActivity.fulfilled, (state, action) => {
        const i = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (i !== -1) state.tasks[i] = action.payload;
        if (state.currentTask?._id === action.payload._id) state.currentTask = action.payload;
      })
      // deleteActivity
      .addCase(deleteActivity.fulfilled, (state, action) => {
        const i = state.tasks.findIndex((t) => t._id === action.payload._id);
        if (i !== -1) state.tasks[i] = action.payload;
        if (state.currentTask?._id === action.payload._id) state.currentTask = action.payload;
      });
  },
});

export const { clearError, clearCurrentTask, setCurrentTask } = tasksSlice.actions;
export default tasksSlice.reducer;
